import { alpha } from '@mui/material/styles';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

export interface TokenPaletteItem {
  /** The chip's label, e.g. `{{Line.PhoneNumber}}`. */
  token: string;
  /**
   * What insertion puts at the caret, when it differs from the label. A list chip reads
   * `Approval.Steps[]` but inserts the `{{#each}}` block that gives its members a scope, and a
   * helper chip inserts placeholder arguments. Defaults to the label.
   */
  insertText?: string;
  /** Required tokens are visually highlighted (dot + accent border). */
  required?: boolean;
}

export interface TokenPaletteGroupProps {
  title: string;
  subtitle?: string;
  items: TokenPaletteItem[];
  onInsert: (token: string) => void;
}

const MONO = '600 11.5px/1 ui-monospace, Menlo, Consolas, monospace';

/**
 * One grouped section of the insert palette (event tokens / customer.* /
 * global.* / helpers). Clicking a chip inserts the token at the editor caret.
 * Port of the prototype `NtePaletteGroup`.
 */
export function TokenPaletteGroup({ title, subtitle, items, onInsert }: TokenPaletteGroupProps) {
  return (
    <Stack spacing={1}>
      <Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.primary',
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {items.map((item) => (
          <ButtonBase
            key={item.token}
            onClick={() => onInsert(item.insertText ?? item.token)}
            title={item.token}
            sx={(theme) => ({
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.625,
              px: 1,
              py: 0.5,
              borderRadius: '6px',
              font: MONO,
              color: item.required ? theme.palette.secondary.dark : theme.palette.info.dark,
              bgcolor: item.required
                ? alpha(theme.palette.secondary.main, 0.08)
                : theme.palette.background.paper,
              border: `1px solid ${
                item.required ? alpha(theme.palette.secondary.main, 0.4) : theme.palette.divider
              }`,
              '&:hover': { borderColor: theme.palette.secondary.main },
            })}
          >
            {item.required ? (
              <Box
                component="span"
                sx={(theme) => ({
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: theme.palette.secondary.main,
                })}
              />
            ) : null}
            {item.token}
          </ButtonBase>
        ))}
      </Box>
    </Stack>
  );
}
